import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useContext, useMemo } from "react";
import { readInlineData, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import {
	MODULE_RATING_INLINE_FRAGMENT,
	QUERY,
	QUERY_FRAGMENT,
} from "./module-rating-details-table.graphql";
import {
	moduleRatingDetailsTable_ModuleRatingInlineFragment$data,
	moduleRatingDetailsTable_ModuleRatingInlineFragment$key,
} from "../../../../../__generated__/moduleRatingDetailsTable_ModuleRatingInlineFragment.graphql";
import { moduleRatingDetailsTable_Query } from "../../../../../__generated__/moduleRatingDetailsTable_Query.graphql";
import { moduleRatingDetailsTable_Refetch } from "../../../../../__generated__/moduleRatingDetailsTable_Refetch.graphql";
import { moduleRatingDetailsTable_RefetchableQueryFragment$key } from "../../../../../__generated__/moduleRatingDetailsTable_RefetchableQueryFragment.graphql";
import { moduleRatingsTable_Refetch } from "../../../../../__generated__/moduleRatingsTable_Refetch.graphql";
import { DateTimeDisplay } from "../../../../../infecto-lms-admin/components/core/datetime/DateTimeDisplay";
import { useDebouncesFilters } from "../../../../../infecto-lms-admin/components/core/filters/useDebouncesFilters";
import { ModuleRatingDetailsContext } from "../../module-rating-details.context";

export const ModuleRatingDetailsTable = ({ id }: { id: string }) => {
	const query = useLazyLoadQuery<moduleRatingDetailsTable_Query>(QUERY, {
		nodeRef: id,
		first: 10,
	});
	const { oneKeyId } = useContext(ModuleRatingDetailsContext);

	const { data, hasPrevious, hasNext, loadPrevious, loadNext, refetch } = usePaginationFragment<
		moduleRatingDetailsTable_Refetch,
		moduleRatingDetailsTable_RefetchableQueryFragment$key
	>(QUERY_FRAGMENT, query);

	const connection = data.Admin.ModuleRating.ModuleRating || [];

	const nodes = useMemo(
		() =>
			connection.edges
				?.filter((e) => !!e?.node)
				.map((e) => e!.node)
				.map((item) =>
					readInlineData<moduleRatingDetailsTable_ModuleRatingInlineFragment$key>(
						MODULE_RATING_INLINE_FRAGMENT,
						item,
					),
				) ?? [],
		[connection],
	);

	const filters = useMemo(
		(): moduleRatingDetailsTable_Refetch["variables"] => ({
			oneKeyId: oneKeyId || undefined,
			nodeRef: id,
		}),
		[oneKeyId],
	);
	useDebouncesFilters<moduleRatingsTable_Refetch["variables"]>(filters, refetch);

	return (
		<div>
			<DataTable<moduleRatingDetailsTable_ModuleRatingInlineFragment$data[]>
				emptyMessage="Keine Modul-Bewertungen"
				className="mb-3"
				value={nodes}
			>
				<Column
					header="Name"
					field="user.name"
					sortable
					body={(item: moduleRatingDetailsTable_ModuleRatingInlineFragment$data) => (
						<div>{item.user?.name}</div>
					)}
				/>
				<Column
					header="One Key"
					body={(item: moduleRatingDetailsTable_ModuleRatingInlineFragment$data) => (
						<div>{item.oneKeyId}</div>
					)}
				/>
				<Column
					header="Bewertung"
					sortable
					field={"value"}
					body={(item: moduleRatingDetailsTable_ModuleRatingInlineFragment$data) => (
						<div>{item.value}</div>
					)}
				/>
				<Column
					header="Time stamp"
					sortable
					field={"createdAt"}
					body={(item: moduleRatingDetailsTable_ModuleRatingInlineFragment$data) => (
						<DateTimeDisplay value={item.createdAt} hideTimezone />
					)}
				/>
				<Column
					header="Kommentar"
					body={(item: moduleRatingDetailsTable_ModuleRatingInlineFragment$data) => (
						<div>{item.message}</div>
					)}
				/>
			</DataTable>
			<div className="flex justify-content-center align-items-center">
				<Button
					disabled={!hasPrevious}
					onClick={() => loadPrevious(20)}
					className="mr-3 p-button-secondary"
				>
					Zurück
				</Button>
				<Button
					className="p-button-secondary"
					disabled={!hasNext}
					onClick={() => loadNext(20)}
				>
					Weiter
				</Button>
			</div>
		</div>
	);
};
